import React, { ReactElement, useCallback, useContext } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import de from "react-phone-input-2/lang/de.json";
import da from "../../../Common/localization/Translations/da-countries.json";
import no from "../../../Common/localization/Translations/no-countries.json";
import sv from "../../../Common/localization/Translations/sv-countries.json";
import { Redirect } from "react-router-dom";
import { QueryContext } from "../../context/query";
import { usePhoneFlow, useTokenFlow } from '../../../Common/hooks';
import "react-phone-input-2/lib/style.css";
import FormDirections from "../../../Common/components/templates/formDirections";
import { globalConfig } from "../../../Common/configuration/config";
import { PasswordlessSMSFlowCommand } from "../../../Common/domain/src/api/command";
import PrimaryButton from "../../../Common/components/templates/buttons/primary";
import { useIntl, FormattedMessage } from 'react-intl';
import { getLocale, getTranslationForCode, getTranslationForCodeWithReplacements } from "../../../Common/localization/localizationHelper";
import SecondaryButton from "../../../Common/components/templates/buttons/secondary";


export const continueState = (auth0state: string, domain: string) => {
    const temp: any = window;
    temp.location = `https://${domain}/continue?state=${auth0state}`;
};

const PhoneNumberInput = (props: any) => {
    const [phone, setPhone] = useState("");
    const { phoneFlow, handleStartPasswordlessFlow } = usePhoneFlow();
    const { tokenFlow, handleStartTokenFlow } = useTokenFlow();
    const { setState } = useContext(QueryContext);
    const config = globalConfig.get();
    let params = new URLSearchParams(props.location.search);
    let token = params.get("token");
    let auth0state = params.get("state");
    const intl = useIntl();
    const locale = getLocale();
    localStorage.setItem("token", token || '');
    localStorage.setItem("auth0state", auth0state || '');

    const [disableSkip, setDisableSkip] = useState(false);
    const startPasswordlessMethod: any = useCallback(
        (token: string, phone: string) =>
            handleStartPasswordlessFlow(
                token, new PasswordlessSMSFlowCommand({ phoneNumber: "+" + phone })
            ),
        [handleStartPasswordlessFlow]
    );

    useEffect(() => {
        if (token !== null && auth0state !== null) {
            handleStartTokenFlow(token ?? "");
        }
    }, []);

    const getPhoneInputTranslated = (lang: string): ReactElement => {
        let country = getInitialCountry(lang);
        const phoneNumberLabel = intl?.messages["passwordless.phoneNumber.label"]?.toString() || "Phone Number";
        if (lang === "en") {
            return (<PhoneInput
                country={country}
                value={phone}
                onChange={handleChange}
                inputProps={{
                    'aria-label': phoneNumberLabel,
                }}
                isValid={(value) => {
                    if (!isValidPhoneNumber("+" + value)) {
                        let message = intl?.messages["passwordless.phoneNumber.error.invalidNumber"]?.toString();
                        return message?.replace("{number}", value);
                    } else {
                        return true;
                    }
                }}
            />)
        }
        return (<PhoneInput
            localization={getPhoneInputLocalization(lang)}
            country={country}
            value={phone}
            inputProps={{
                'aria-label': phoneNumberLabel,
            }}
            onChange={handleChange}
            isValid={(value) => {
                if (!isValidPhoneNumber("+" + value)) {
                    let message = intl?.messages["passwordless.phoneNumber.error.invalidNumber"]?.toString();
                    return message?.replace("{number}", value);
                } else {
                    return true;
                }
            }}
        />)
    };

    const handleSubmit = () => {
        if (isValidPhoneNumber("+" + phone)) {
            startPasswordlessMethod(token, phone);
        }
    };
    const handleChange = (value: string) => {
        setPhone(value);
        localStorage.setItem("phoneNumber", "+" + value || '');
        setState({
            token: token!,
            auth0state: auth0state!,
            phoneNumber: "+" + value,
            email: tokenFlow.loggedUser.data.email
        });
    };

    const renderRedirect = () => {
        if (phoneFlow.result.isValid) {
            let redirect = "/verification"
            let locale = getLocale();
            if (locale) {
                redirect += `?ui_locales=${locale}`;
            }
            return <Redirect to={redirect} />;
        }
    };

    const getInitialCountry = (lang: string): string => {
        if (globalConfig.config.Region === "US") {
            return 'us';
        }
        if (globalConfig.config.Region === "AU") {
            return 'au';
        }
        if (lang === 'de') {
            return 'de';
        }
        if (lang === 'da') {
            return 'dk';
        }
        if (lang === 'no') {
            return 'no';
        }
        if (lang === 'sv') {
            return 'se';
        }
        return 'gb';
    }
    const getPhoneInputLocalization = (lang: string): any => {
        switch (lang) {
            case 'de':
                return de;
            case 'da':
                return da;
            case 'no':
                return no;
            case 'sv':
                return sv;
            default:
                return undefined;
        }
    }

    return (
        <form className="form">
            <FormDirections>
                <FormattedMessage id="passwordless.phoneNumber.header"></FormattedMessage>
            </FormDirections>
            {tokenFlow.loggedUser.message ||
                phoneFlow.result.message ? (
                    <div className="error">
                    {" "}
                    {tokenFlow.loggedUser.message ? getTranslationForCode(tokenFlow.loggedUser.errorCode, intl) : ""}
                    {phoneFlow.result.message ? getTranslationForCodeWithReplacements(phoneFlow.result.errorCode, intl, { number: phone }) : ""}
                    {" "}
                </div>
            ) : null}
            <div className="content">
                <label htmlFor="phone-input" className="label">
                    <FormattedMessage id="passwordless.phoneNumber.label" />
                </label>
                {getPhoneInputTranslated(locale)}

                <div className="buttons">
                    {renderRedirect()}
                    <SecondaryButton
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!tokenFlow.loggedUser.isValid}
                    >
                        <FormattedMessage id="continue"></FormattedMessage>
                    </SecondaryButton>
                    <PrimaryButton formNoValidate
                        variant="contained"
                        disabled={disableSkip}
                        onClick={(e: any) => {
                            setDisableSkip(true);
                            e.stopPropagation();
                            if (auth0state) continueState(auth0state, config.Domain);
                        }}
                    >
                        <FormattedMessage id="skipStep"></FormattedMessage>
                    </PrimaryButton>
                </div>
            </div>
        </form>
    );
};

export default PhoneNumberInput; 
